import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useMatch, useResolvedPath } from "react-router-dom";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";

import useIsMobile from "hooks/use-is-mobile.hook";
import usePersistedState from "hooks/use-persisted-state";

import { SideMenuFlyoutProvider } from "./side-menu-flyout.component";
import SideMenuGroupHeading from "./side-menu-group-heading.component";
import SideMenuHeader from "./side-menu-header.component";
import SideMenuItem, { SideMenuItemProps } from "./side-menu-item.component";
import SideMenuScrollCue from "./side-menu-scroll-cue.component";
import useSupportWidgetsInFooter from "./use-support-widgets-in-footer.hook";

/**
 * The collapsed menu: icons only, 72px. Wide enough for a comfortable 60px target
 * per row and for the Intercom launcher (48px, 20px in from the left) to sit
 * inside it rather than hang over the page.
 */
export const SIDE_MENU_RAIL_WIDTH_PX = 72;
/**
 * Expanded, the menu is as wide as its longest visible label needs (`fit-content`),
 * never narrower than this — a typical practice lands exactly here.
 */
export const SIDE_MENU_MIN_WIDTH_PX = 144;
/**
 * The Intercom launcher (48px, 20px from the bottom) floats over the bottom-left
 * corner. This much of the footer stays empty so it never covers the last row. The
 * Marker.io Help button, which used to stack above it, is a footer row instead
 * (`useSupportWidgetsInFooter`).
 */
export const SIDE_MENU_WIDGET_CLEARANCE_PX = 76;
/**
 * Per-browser, like the other layout preferences; see `usePersistedState`. One
 * preference for every menu built on the shell, so the rail follows the person
 * from the main app into Administration or Leads and back.
 */
export const SIDE_MENU_COLLAPSED_STORAGE_KEY = "sideMenuCollapsed";

/** One row, keyed; the shell supplies `collapsed`, `isDefaultPath` and `group`. */
export type SideMenuRow = Omit<
  SideMenuItemProps,
  "collapsed" | "isDefaultPath" | "group"
> & {
  key: string;
};

/** A run of rows under an optional heading ("FRONT DESK"); untitled for Home. */
export type SideMenuSection = {
  key: string;
  title?: string;
  rows: SideMenuRow[];
};

type SideMenuShellProps = {
  /** The `<nav>`'s accessible name: "Main", "Administration", "Leads". */
  navLabel: string;
  sections: SideMenuSection[];
  /** Pinned under the list; Help joins them last where Marker.io is configured. */
  footerRows?: SideMenuRow[];
  showTour?: boolean;
  showSearch?: boolean;
  onSearch?: () => void;
};

const NO_ROWS: SideMenuRow[] = [];

const hiddenScrollbarSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" }
};

/**
 * The scrolling list between header and footer, with its "more below" cue. When
 * the list is taller than the window the cue names the groups out of view, and
 * the current page is scrolled into view on every navigation, so nobody has to
 * hunt for where they are.
 */
function SideMenuScrollArea({
  navLabel,
  sections,
  collapsed,
  renderRow
}: {
  navLabel: string;
  sections: SideMenuSection[];
  collapsed: boolean;
  renderRow: (row: SideMenuRow, group?: string) => JSX.Element;
}) {
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const contentKey = useMemo(
    () =>
      `${collapsed}:${sections
        .map(
          (section) =>
            `${section.key}:${section.rows
              .map((row) =>
                typeof row.badgeContent === "number" ? row.badgeContent : 0
              )
              .join(".")}`
        )
        .join(",")}`,
    [collapsed, sections]
  );

  // Land on the current page: a deep link to Reports must not open on a menu whose
  // highlighted row is below the fold.
  useEffect(() => {
    const nav = navRef.current;
    const current = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !current || typeof current.scrollIntoView !== "function") {
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const rowRect = current.getBoundingClientRect();
    if (rowRect.top < navRect.top || rowRect.bottom > navRect.bottom - 40) {
      current.scrollIntoView({ block: "center" });
    }
  }, [pathname, contentKey]);

  return (
    <Box
      sx={{
        position: "relative",
        flex: "1 1 0",
        minHeight: 0,
        display: "flex"
      }}
    >
      {/* The list scrolls on its own when it is taller than the window; header and
          footer stay put. */}
      <Box
        ref={navRef}
        component="nav"
        aria-label={navLabel}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          px: 0.75,
          py: 0.25,
          // Room under the last row for a cue to sit over air, not over a row, once
          // the list is scrolled to the end. Constant, so nothing here depends on
          // the scroll position and the rows never re-render while it scrolls.
          pb: 1.5,
          ...hiddenScrollbarSx
        }}
      >
        {sections.map((section) => (
          <List
            key={section.key}
            disablePadding
            aria-label={section.title}
            data-side-menu-section={section.title ?? ""}
          >
            {section.title && (
              <SideMenuGroupHeading
                title={section.title}
                collapsed={collapsed}
              />
            )}
            {section.rows.map((row) => renderRow(row, section.title))}
          </List>
        ))}
      </Box>
      <SideMenuScrollCue
        navRef={navRef}
        contentKey={contentKey}
        collapsed={collapsed}
      />
    </Box>
  );
}

/**
 * The frame every side menu shares (ENG-2535): logo and collapse toggle, grouped
 * rows under hairline headings, a pinned footer, the two states (expanded,
 * fit-to-label; a 72px icon rail whose hover card names each row), and the phone
 * drawer. The main app, Administration and Leads each decide what the rows are;
 * this decides how they look and behave.
 */
export default function SideMenuShell({
  navLabel,
  sections,
  footerRows = NO_ROWS,
  showTour = false,
  showSearch = false,
  onSearch
}: SideMenuShellProps) {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const resolved = useResolvedPath("/");
  const isDefaultPath = Boolean(
    useMatch({ path: resolved.pathname, end: true })
  );

  const [showMenu, setShowMenu] = useState(!isMobile);

  useEffect(() => {
    setShowMenu(!isMobile);
  }, [isMobile]);
  // The rail is a desktop preference; on a phone the menu is a drawer over the page
  // and always shows its labels.
  const [collapsedPreference, setCollapsedPreference] =
    usePersistedState<boolean>(SIDE_MENU_COLLAPSED_STORAGE_KEY, false);
  const collapsed = !isMobile && collapsedPreference;

  const { helpAvailable, openHelp } = useSupportWidgetsInFooter();

  const layoutSx = useMemo(() => {
    return {
      width: collapsed ? `${SIDE_MENU_RAIL_WIDTH_PX}px` : "fit-content",
      minWidth: collapsed
        ? `${SIDE_MENU_RAIL_WIDTH_PX}px`
        : `${SIDE_MENU_MIN_WIDTH_PX}px`,
      height: "100vh",
      flex: "0 0 auto",
      display: showMenu ? "flex" : "none",
      flexDirection: "column",
      overflow: "hidden",
      background: theme.palette.sideMenu.main,
      color: theme.palette.neutral[500],
      position: isMobile ? "absolute" : "relative",
      zIndex: 3,
      boxShadow: isMobile ? "20px 0 15px -12px rgba(0, 0, 0, 0.7)" : "none"
    };
  }, [
    theme.palette.sideMenu.main,
    theme.palette.neutral,
    isMobile,
    showMenu,
    collapsed
  ]);

  const renderRow = ({ key, ...row }: SideMenuRow, group?: string) => (
    <SideMenuItem
      key={key}
      isDefaultPath={isDefaultPath}
      collapsed={collapsed}
      group={group}
      {...row}
    />
  );

  if (isMobile && !showMenu) {
    return (
      <Box
        sx={{
          position: "absolute",
          left: 2,
          top: 2,
          // Above the practice-context banner, which otherwise covers it in an admin
          // session and leaves no way to open the menu on a phone.
          zIndex: 3
        }}
      >
        <Button
          sx={{
            background: theme.palette.sideMenu.main,
            color: "white",
            minWidth: "38px",
            minHeight: "38px",
            boxShadow: "2px 2px 7px 2px rgba(0, 0, 0, 0.4)"
          }}
          style={{ padding: 0 }}
          size="small"
          aria-label="Open menu"
          onClick={() => setShowMenu(true)}
        >
          <MenuIcon sx={{ width: "27px", height: "27px" }} />
        </Button>
      </Box>
    );
  }

  return (
    <SideMenuFlyoutProvider resetKey={collapsed}>
      <ClickAwayListener
        onClickAway={() => {
          if (isMobile) {
            setShowMenu(false);
          }
        }}
      >
        <Stack
          sx={layoutSx}
          data-testid="layout-side-menu"
          data-collapsed={collapsed ? "true" : "false"}
        >
          <SideMenuHeader
            collapsed={collapsed}
            isMobile={isMobile}
            onToggle={() =>
              isMobile
                ? setShowMenu(false)
                : setCollapsedPreference(!collapsedPreference)
            }
            showTour={showTour}
            showSearch={showSearch && onSearch !== undefined}
            onSearch={() => onSearch?.()}
          />

          <SideMenuScrollArea
            navLabel={navLabel}
            sections={sections}
            collapsed={collapsed}
            renderRow={renderRow}
          />

          <Box
            data-testid="side-menu-footer"
            sx={{
              flex: "0 0 auto",
              borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
              pt: 0.75,
              px: 0.75
            }}
          >
            <List disablePadding>
              {footerRows.map((row) => renderRow(row))}
              {helpAvailable && (
                <SideMenuItem
                  onClick={openHelp}
                  primary="Help"
                  Icon={HelpOutlineIcon}
                  tooltip="Report a problem or send Suno feedback, with a screenshot"
                  collapsed={collapsed}
                />
              )}
            </List>
            <Box
              aria-hidden="true"
              title="Space kept clear for the Intercom launcher"
              sx={{ height: `${SIDE_MENU_WIDGET_CLEARANCE_PX}px` }}
            />
          </Box>
        </Stack>
      </ClickAwayListener>
    </SideMenuFlyoutProvider>
  );
}
